import {
  formatMoney,
  overBudgetLabel,
  overBudgetPercent,
  salaryBudgetProblem,
  toMinorUnits,
  type CurrencyCode,
} from '@nest-recruit/shared';
import { EMPTY } from '../../../lib/format';

// The pure half of Basics (Job-salary-budget.dc.html, Redesign Group 2): its four tabs, what each
// still needs (the amber "N to fix"), the salary budget's mode and checks, and the "Try it" box.
// Kept out of the component so each rule is tested on its own.

export const BASICS_TABS = ['hire', 'people', 'budget', 'more'] as const;
export type BasicsTab = (typeof BASICS_TABS)[number];

export const BASICS_TAB_LABELS: Readonly<Record<BasicsTab, string>> = {
  hire: 'The hire',
  people: 'People',
  budget: 'Salary budget',
  more: 'More about the job',
};

export const BASICS_TAB_DESCRIPTIONS: Readonly<Record<BasicsTab, string>> = {
  hire: 'What the job is and how many people it needs.',
  people: 'Requested by and Line manager get the shortlist.',
  budget: 'What this job can pay. Leave it empty if there is no budget.',
  more: 'What candidates see, dates, the shift and the advert.',
};

export function isBasicsTab(value: string): value is BasicsTab {
  return BASICS_TABS.some((tab) => tab === value);
}

// --- What each tab still needs ------------------------------------------------------------------

export const MAX_HEADCOUNT = 50;
/** `updateJobBasicsSchema.publicTitle`: at least two characters. */
const MIN_PUBLIC_TITLE = 2;

/** Which tab a basics field lives on, so a field the server refused counts on its tab. */
const FIELD_TABS: Readonly<Record<string, BasicsTab>> = {
  title: 'hire',
  departmentId: 'hire',
  slotsTotal: 'hire',
  requestedByUserId: 'people',
  lineManagerUserId: 'people',
  hiringManagerUserId: 'people',
  recruiterUserId: 'people',
  workMode: 'people',
  location: 'people',
  budgetMin: 'budget',
  budgetMax: 'budget',
};

/** Fields that share one message count once: the budget's two boxes, the shift's three. */
const FIELD_PROBLEM_KEYS: Readonly<Record<string, string>> = {
  budgetMin: 'budget',
  budgetMax: 'budget',
  shiftStart: 'shift',
  shiftEnd: 'shift',
  shiftDisplayZone: 'shift',
};

export interface BasicsCheck {
  /** A direct job edits its title, department and headcount; a requisition's are locked (E3). */
  readonly isDirect: boolean;
  readonly title: string;
  /** What is typed in "People needed". */
  readonly slotsText: string;
  readonly requestedByUserId: string | null;
  readonly lineManagerUserId: string | null;
  readonly recruiterUserId: string | null;
  readonly publicTitle: string;
  readonly shiftStart: string | null;
  readonly shiftEnd: string | null;
  readonly shiftDisplayZone: string | null;
  /** The budget's one message, or null (`checkBudget`). */
  readonly budgetMessage: string | null;
  /** Fields the last save was refused for. */
  readonly refusedFields: readonly string[];
}

export function isHeadcount(text: string): boolean {
  const slots = Number(text);
  return text.trim() !== '' && Number.isInteger(slots) && slots >= 1 && slots <= MAX_HEADCOUNT;
}

/**
 * The amber count on each tab: the required things still empty and the checks still failing,
 * the same list the section's tick reads (`jobSectionStates`), plus anything the server refused.
 */
export function basicsTabProblems(check: BasicsCheck): Readonly<Record<BasicsTab, number>> {
  const problems: Record<BasicsTab, Set<string>> = { hire: new Set(), people: new Set(), budget: new Set(), more: new Set() };
  if (check.isDirect) {
    if (check.title.trim() === '') problems.hire.add('title');
    if (!isHeadcount(check.slotsText)) problems.hire.add('slotsTotal');
  }
  if (check.requestedByUserId === null) problems.people.add('requestedByUserId');
  if (check.lineManagerUserId === null) problems.people.add('lineManagerUserId');
  if (check.recruiterUserId === null) problems.people.add('recruiterUserId');
  if (check.budgetMessage !== null) problems.budget.add('budget');
  if (check.publicTitle.trim().length < MIN_PUBLIC_TITLE) problems.more.add('publicTitle');
  if (check.shiftStart !== null && (check.shiftEnd === null || check.shiftDisplayZone === null)) problems.more.add('shift');
  for (const field of check.refusedFields) {
    problems[FIELD_TABS[field] ?? 'more'].add(FIELD_PROBLEM_KEYS[field] ?? field);
  }
  return { hire: problems.hire.size, people: problems.people.size, budget: problems.budget.size, more: problems.more.size };
}

// --- The salary budget --------------------------------------------------------------------------

/** Not stored: read from which figures are set (approved mockup; Redesign Group 2). */
export type BudgetMode = 'range' | 'top' | 'none';

export const BUDGET_MODE_LABELS: Readonly<Record<BudgetMode, string>> = {
  range: 'A range',
  top: 'Only a top figure',
  none: 'No budget',
};

/** From the figures as typed ("" when empty). A from with no top is still a range; it gives no mark yet. */
export function budgetModeOf(minDigits: string, maxDigits: string): BudgetMode {
  if (minDigits !== '') return 'range';
  return maxDigits === '' ? 'none' : 'top';
}

/** "" is no figure; "0" is not a budget. Digits only reach here (`MoneyInput`). */
export function wholeOf(digits: string): number | null | undefined {
  if (digits === '') return null;
  const value = Number(digits);
  return Number.isSafeInteger(value) && value > 0 ? value : undefined;
}

export interface BudgetCheck {
  /** Whole units; null when empty or not usable. */
  readonly min: number | null;
  readonly max: number | null;
  /** One message for the pair, under both boxes. */
  readonly message: string | null;
  readonly isMinBad: boolean;
  readonly isMaxBad: boolean;
}

const ABOVE_ZERO = 'Enter an amount above 0.';

export function checkBudget(minDigits: string, maxDigits: string): BudgetCheck {
  const min = wholeOf(minDigits);
  const max = wholeOf(maxDigits);
  if (min === undefined || max === undefined) {
    return { min: min ?? null, max: max ?? null, message: ABOVE_ZERO, isMinBad: min === undefined, isMaxBad: max === undefined };
  }
  const problem = salaryBudgetProblem(min, max);
  // The top below the from: both boxes red, one message (mockup `.money.bad`).
  if (problem) return { min, max, message: problem.message, isMinBad: true, isMaxBad: true };
  return { min, max, message: null, isMinBad: false, isMaxBad: false };
}

// --- "Try it: someone asks" ---------------------------------------------------------------------

export interface TryItResult {
  /** "৳85,000", or "—" before anything is typed. */
  readonly asksText: string;
  readonly isOver: boolean;
  /** "25% over budget"; empty unless over. */
  readonly overLabel: string;
  readonly note: string;
}

/**
 * What the expected-salary mark would say for an ask of `asksDigits` against a top of `topWhole`.
 * The sums are the shared `overBudgetPercent` on minor units (#7), so this box can never disagree
 * with the mark on a real candidate.
 */
export function tryItResult(asksDigits: string, topWhole: number | null, currency: CurrencyCode): TryItResult {
  const asks = wholeOf(asksDigits);
  if (asks === null || asks === undefined) return { asksText: EMPTY, isOver: false, overLabel: '', note: 'Type a figure to see the mark.' };
  const asksMinor = toMinorUnits(asks, currency);
  const asksText = formatMoney({ amountMinor: asksMinor, currency });
  if (topWhole === null) return { asksText, isOver: false, overLabel: '', note: 'No budget, so no mark.' };
  const topMinor = toMinorUnits(topWhole, currency);
  const percent = overBudgetPercent(asksMinor, currency, topMinor, currency);
  if (percent === null) return { asksText, isOver: false, overLabel: '', note: 'Within budget. No mark.' };
  return {
    asksText,
    isOver: true,
    overLabel: overBudgetLabel(percent),
    note: `${formatMoney({ amountMinor: asksMinor - topMinor, currency })} more than the top of ${formatMoney({ amountMinor: topMinor, currency })}`,
  };
}
